/**
 * Dynamic Discount Badge Updater
 * Updates discount percentage in badges when products are selected in collections
 */

class DynamicDiscountBadge {
  constructor() {
    this.badgeSelector = '.discount-badge-dynm__percent-value';
    this.productSelector = '[data-product-id], [data-product-form], form[action*="/cart/add"], .product-item, .collection-product';
    this.activeProductClass = 'product-item--active';
    
    this.init();
  }

  init() {
    this.bindEvents();
    this.updateBadgeOnLoad();
  }

  /**
   * Bind events for product selection changes
   */
  bindEvents() {
    // Listen for product selection in collections
    document.addEventListener('pointerdown', (e) => {
      const productElement = e.target.closest(this.productSelector);
      if (productElement) {
        this.handleProductSelection(productElement);
      }
    });
    document.addEventListener('click', (e) => {
      const productElement = e.target.closest(this.productSelector);
      if (productElement) {
        this.handleProductSelection(productElement);
      }
    });

    // Listen for variant changes within products
    document.addEventListener('change', (e) => {
      if (e.target.matches('[name="id"], .variant-selector, [data-variant-selector], select[name^="variant"]')) {
        const productElement = e.target.closest(this.productSelector);
        if (productElement) {
          this.handleProductSelection(productElement);
        }
      }
    });

    // Handle Shopify's native variant selection events
    document.addEventListener('shopify:variant:change', (e) => {
      const productElement = e.target.closest(this.productSelector);
      if (productElement) {
        this.handleProductSelection(productElement);
      }
    });

    // Handle AJAX product loads (common in Shopify themes)
    this.observeProductChanges();
    
    // Handle Shopify section events
    this.handleShopifySectionEvents();
  }

  /**
   * Handle product selection and update badge
   */
  handleProductSelection(productElement) {
    this.scheduleUpdate(productElement);
  }

  scheduleUpdate(productElement) {
    if (this.pendingUpdate) {
      cancelAnimationFrame(this.pendingUpdate);
    }
    this.pendingElement = productElement;
    this.pendingUpdate = requestAnimationFrame(() => {
      this.pendingUpdate = requestAnimationFrame(() => {
        const productData = this.extractProductData(this.pendingElement);
        if (productData) {
          this.updateBadge(productData);
          this.markProductAsActive(this.pendingElement);
        }
      });
    });
  }

  /**
   * Extract product data from DOM element
   */
  extractProductData(productElement) {
    const productId = productElement?.dataset?.productId || 
                     productElement?.querySelector?.('[data-product-id]')?.dataset?.productId;
    const badgeElement = this.getBadgeElement(productElement);
    const variantDiscounts = badgeElement?.dataset?.variantDiscounts;
    const selectedVariantId = this.getSelectedVariantId(productElement);

    if (variantDiscounts) {
      const variantData = this.getVariantDataById(variantDiscounts, selectedVariantId);
      if (variantData) {
        return {
          productId,
          price: variantData.price,
          compareAtPrice: variantData.compareAtPrice
        };
      }
    }

    const price = this.extractPrice(productElement);
    const compareAtPrice = this.extractCompareAtPrice(productElement);
    
    if (price && compareAtPrice && compareAtPrice > price) {
      return {
        productId,
        price,
        compareAtPrice
      };
    }
    
    return null;
  }

  getSelectedVariantId(productElement) {
    const variantInput = productElement?.querySelector?.('[name="id"]') || document.querySelector('[name="id"]');
    const value = variantInput?.value;
    if (value && value !== 'not-selected') {
      return value;
    }
    return null;
  }

  getBadgeElement(productElement) {
    if (productElement && productElement.classList && productElement.classList.contains('discount-badge-dynm') && productElement.hasAttribute('data-variant-discounts')) {
      return productElement;
    }
    return productElement?.querySelector?.('.discount-badge-dynm[data-variant-discounts]') || document.querySelector('.discount-badge-dynm[data-variant-discounts]');
  }

  getVariantDataById(variantDiscounts, selectedVariantId) {
    const variants = variantDiscounts.split('|').filter(Boolean);
    if (!variants.length) return null;
    const match = selectedVariantId
      ? variants.find((variantData) => variantData.split(':')[0] === String(selectedVariantId))
      : null;
    const data = match || variants[0];
    const parts = data.split(':');
    if (parts.length < 4) return null;
    const price = parseFloat(parts[2]);
    const compareAtPrice = parseFloat(parts[3]);
    if (!price || !compareAtPrice) return null;
    return { price, compareAtPrice };
  }

  /**
   * Extract current price from product element
   */
  extractPrice(productElement) {
    // Check selected option first (if available)
    const select = productElement?.querySelector?.('select[name="id"]') || document.querySelector('select[name="id"]');
    if (select?.options?.[select.selectedIndex]?.dataset?.price) {
      const price = this.parsePrice(select.options[select.selectedIndex].dataset.price);
      if (price) return price;
    }

    // Try multiple selectors for price elements
    const priceSelectors = [
      '.price--current .price-item, .price-item--regular, [data-price]',
      '.product-price--current',
      '.price .money',
      '[data-product-price]'
    ];
    
    for (const selector of priceSelectors) {
      const priceElement = productElement.querySelector(selector);
      if (priceElement) {
        const priceText = priceElement.textContent || priceElement.dataset.price;
        const price = this.parsePrice(priceText);
        if (price) return price;
      }
    }
    
    return null;
  }

  /**
   * Extract compare at price from product element
   */
  extractCompareAtPrice(productElement) {
    // Check selected option first (if available)
    const select = productElement?.querySelector?.('select[name="id"]') || document.querySelector('select[name="id"]');
    if (select?.options?.[select.selectedIndex]?.dataset?.comparePrice) {
      const comparePrice = this.parsePrice(select.options[select.selectedIndex].dataset.comparePrice);
      if (comparePrice) return comparePrice;
    }

    // Try multiple selectors for compare at price elements
    const compareAtPriceSelectors = [
      '.price--compare .price-item, .price-item--compare, [data-compare-price]',
      '.product-compare-price',
      '.compare-price .money',
      '[data-product-compare-price]'
    ];
    
    for (const selector of compareAtPriceSelectors) {
      const compareElement = productElement.querySelector(selector);
      if (compareElement) {
        const compareText = compareElement.textContent || compareElement.dataset.comparePrice;
        const comparePrice = this.parsePrice(compareText);
        if (comparePrice) return comparePrice;
      }
    }
    
    return null;
  }

  /**
   * Parse price text to number
   */
  parsePrice(priceText) {
    if (!priceText) return null;
    
    // Remove currency symbols and format
    const cleaned = priceText
      .replace(/[^\d,.]/g, '')
      .replace(/,/g, '.')
      .replace(/\.$/, '');
    
    const price = parseFloat(cleaned);
    return isNaN(price) ? null : price;
  }

  /**
   * Calculate discount percentage with validation
   */
  calculateDiscountPercentage(price, compareAtPrice) {
    // Input validation
    if (!price || !compareAtPrice || 
        typeof price !== 'number' || 
        typeof compareAtPrice !== 'number' ||
        isNaN(price) || 
        isNaN(compareAtPrice)) {
      console.warn('DynamicDiscountBadge: Invalid price data', { price, compareAtPrice });
      return 0;
    }
    
    // Ensure prices are positive
    if (price <= 0 || compareAtPrice <= 0) {
      console.warn('DynamicDiscountBadge: Prices must be positive', { price, compareAtPrice });
      return 0;
    }
    
    // Check if compare price is actually higher than current price
    if (compareAtPrice <= price) {
      return 0;
    }
    
    const discount = compareAtPrice - price;
    const percentage = (discount / compareAtPrice) * 100;
    
    // Validate and ensure integer between 0-100
    const validatedPercentage = Math.max(0, Math.min(100, Math.round(percentage)));
    
    // Additional validation for edge cases
    if (isNaN(validatedPercentage) || !isFinite(validatedPercentage)) {
      console.warn('DynamicDiscountBadge: Calculated percentage is invalid', { 
        price, compareAtPrice, percentage, validatedPercentage 
      });
      return 0;
    }
    
    return validatedPercentage;
  }

  /**
   * Update badge with new discount percentage
   */
  updateBadge(productData) {
    const discountPercentage = this.calculateDiscountPercentage(
      productData.price, 
      productData.compareAtPrice
    );
    
    let targetBadges = [];
    let targetContainers = [];

    // Try to find specific badges for this product
    if (productData.productId) {
      const specificContainers = document.querySelectorAll(`.discount-badge-dynm[data-product-id="${productData.productId}"]`);
      if (specificContainers.length > 0) {
        targetContainers = Array.from(specificContainers);
        targetContainers.forEach(container => {
          const badge = container.querySelector(this.badgeSelector);
          if (badge) targetBadges.push(badge);
        });
      }
    }

    // Fallback to global update if no specific badges found (legacy behavior)
    if (targetBadges.length === 0) {
      targetBadges = document.querySelectorAll(this.badgeSelector);
      targetContainers = document.querySelectorAll('.discount-badge-dynm');
    }

    targetBadges.forEach(badge => {
      badge.textContent = discountPercentage;
      
      // Add animation class for smooth transition
      badge.classList.add('discount-badge-updating');
      setTimeout(() => badge.classList.remove('discount-badge-updating'), 300);
    });
    
    // Hide badge if no discount
    targetContainers.forEach(container => {
      if (discountPercentage === 0) {
        container.style.display = 'none';
      } else {
        container.style.display = 'flex';
      }
    });
  }

  /**
   * Mark product as active
   */
  markProductAsActive(productElement) {
    // Remove active class from all products
    document.querySelectorAll(this.productSelector).forEach(el => {
      el.classList.remove(this.activeProductClass);
    });
    
    // Add active class to selected product
    productElement.classList.add(this.activeProductClass);
  }

  /**
   * Update badge on initial load
   */
  updateBadgeOnLoad() {
    const products = document.querySelectorAll(this.productSelector);
    for (const product of products) {
      const productData = this.extractProductData(product);
      if (productData && productData.compareAtPrice > productData.price) {
        this.updateBadge(productData);
      }
    }
  }

  /**
   * Observe DOM changes for dynamically loaded products
   */
  observeProductChanges() {
    if (typeof MutationObserver !== 'undefined') {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1 && node.matches && node.matches(this.productSelector)) {
              // New product added, check if it should update the badge
              this.handleProductSelection(node);
            }
          });
        });
      });
      
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }
  }

  /**
   * Handle Shopify section events
   */
  handleShopifySectionEvents() {
    // Listen for Shopify section load events
    document.addEventListener('shopify:section:load', (e) => {
      // Re-initialize badge updates for newly loaded sections
      setTimeout(() => {
        this.updateBadgeOnLoad();
      }, 100);
    });

    // Handle collection filtering/sorting
    document.addEventListener('collection:updated', () => {
      setTimeout(() => {
        this.updateBadgeOnLoad();
      }, 100);
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new DynamicDiscountBadge();
  });
} else {
  new DynamicDiscountBadge();
}
